import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import DialogTransition from 'src/components/DialogTransition';
import { useDispatch, useSelector } from 'react-redux';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  loadClientContracts,
  LOAD_CLIENT_CONTRACTS
} from 'src/store/actions/clientActions';
import {
  loadPayoffAmountAction,
  LOAD_PAYOFF_AMOUNT,
  CLEAR_RENT_AFTER_CONTRIBUTION,
  createCapitalContributionFromPaymentAction,
  CREATE_CAPITAL_CONTRIBUTION_FROM_PAYMENT
} from 'src/store/actions/contractActions';
import {
  loadParameter,
  LOAD_PARAMETER,
  CLEAR_PARAMETER
} from 'src/store/actions/parameterActions';
import { selectIndividualParameter } from 'src/store/selectors/parameterSelector';
import { selectClientContractList } from 'src/store/selectors/clientSelectors';
import {
  getContractDescription,
  isPayoffExceedsUnreconciled,
  isSubmitDisabled
} from 'src/utils/contracts';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import ContractStatus from 'src/utils/constants/contracts';
import Loading from 'src/components/Loading';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from '../ReconcilePaymentModal/styles';

const CreateCapitalContributionModal = ({
  open,
  onClose,
  paymentData,
  otherPayments,
  reportId
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const resetRef = useRef();
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [relatedPayments, setRelatedPayments] = useState([]);
  const contracts = useSelector(selectClientContractList);
  const unreconciledAmount = paymentData?.unreconciledAmount ?? 0;
  const toleranceAmount = Number(useSelector(selectIndividualParameter)) || 0;

  const isLoadingContracts = useSelector(
    isLoadingSelector([LOAD_CLIENT_CONTRACTS.action])
  );
  const isLoadingSubmit = useSelector(
    isLoadingSelector([CREATE_CAPITAL_CONTRIBUTION_FROM_PAYMENT.action])
  );
  const isLoadingPayoffAmount = useSelector(
    isLoadingSelector([LOAD_PAYOFF_AMOUNT.action])
  );
  const isLoadingTolerance = useSelector(
    isLoadingSelector([LOAD_PARAMETER.action])
  );

  useEffect(() => {
    if (open && paymentData?.client?.id) {
      const startAt = momentLocalTimezone().subtract(1, 'month').toISOString();

      dispatch(
        loadClientContracts(
          {
            clientId: paymentData.client.id,
            status: ContractStatus.IN_PROGRESS,
            startAt
          },
          false
        )
      );
      dispatch(loadParameter('PAYMENT_TOLERANCE_AMOUNT'));
    }
    if (!open) {
      dispatch({ type: CLEAR_RENT_AFTER_CONTRIBUTION });
      dispatch({ type: CLEAR_PARAMETER });
    }
  }, [dispatch, open, paymentData?.client?.id]);

  const handleFormSubmit = values => {
    dispatch(
      createCapitalContributionFromPaymentAction(
        paymentData.id,
        {
          contractId: values.contract?.id,
          isPayoffRequest: Boolean(values.isPayoffRequest),
          amount: Number(values.amount),
          relatedPaymentIds: values.isPayoffRequest
            ? relatedPayments.map(payment => payment.id)
            : []
        },
        () => {
          setRelatedPayments([]);
          onClose();
        },
        reportId
      )
    );
  };

  const handleClose = () => {
    setAttemptedSubmit(false);
    setRelatedPayments([]);
    if (resetRef.current) resetRef.current();
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      keepMounted
      TransitionComponent={DialogTransition}
      PaperProps={{ sx: { width: '100%', maxWidth: 510 } }}
    >
      <DialogTitle className={classes.header}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            className={classes.header}
            color="primary"
            variant="h2"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {t('SANTANDER_REPORTS.CREATE_CAPITAL_CONTRIBUTION')}
          </Typography>
          <IconButton
            aria-label="Close"
            color="inherit"
            onClick={handleClose}
            className={classes.closeIcon}
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isLoadingSubmit ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200
            }}
          >
            <Loading />
          </Box>
        ) : (
          <Formik
            initialValues={makeInitialState(unreconciledAmount)}
            enableReinitialize
            validationSchema={validationSchema(t, unreconciledAmount, toleranceAmount)}
            onSubmit={handleFormSubmit}
          >
            {({
              errors,
              handleSubmit,
              handleBlur,
              touched,
              values,
              resetForm,
              setFieldValue,
              submitCount
            }) => {
              resetRef.current = () => resetForm();
              const relatedPaymentsTotal = relatedPayments.reduce(
                (sum, payment) => sum + (Number(payment.unreconciledAmount) || 0),
                0
              );
              const combinedUnreconciledAmount = values.isPayoffRequest
                ? unreconciledAmount + relatedPaymentsTotal
                : unreconciledAmount;
              const payoffExceedsUnreconciled = isPayoffExceedsUnreconciled(
                values,
                combinedUnreconciledAmount,
                isLoadingPayoffAmount,
                toleranceAmount
              );
              const submitDisabled =
                isLoadingTolerance ||
                isSubmitDisabled(
                  values,
                  combinedUnreconciledAmount,
                  isLoadingPayoffAmount,
                  isLoadingSubmit,
                  toleranceAmount
                );
              const amount = Number(values.amount) || 0;
              const toleranceApplied =
                !values.isPayoffRequest &&
                toleranceAmount > 0 &&
                amount > unreconciledAmount &&
                amount <= unreconciledAmount + toleranceAmount;

              const showAmountError =
                errors.amount &&
                (touched.amount ||
                  submitCount > 0 ||
                  attemptedSubmit ||
                  values.isPayoffRequest);
              return (
                <Form
                  onSubmit={e => {
                    setAttemptedSubmit(true);
                    handleSubmit(e);
                  }}
                  sx={{ width: '100%' }}
                >

                  {payoffExceedsUnreconciled && (
                    <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                      {t('SANTANDER_REPORTS.PAYOFF_EXCEEDS_UNRECONCILED')}
                    </Alert>
                  )}

                  {toleranceApplied && (
                    <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                      {t('SANTANDER_REPORTS.TOLERANCE_APPLIED', {
                        amount: formatAmountWithDecimals(
                          amount - unreconciledAmount,
                          2
                        )
                      })}
                    </Alert>
                  )}

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 2, mb: 1 }}
                  >
                    {`${t('SANTANDER_REPORTS.UNRECONCILED_AMOUNT')}: $${formatAmountWithDecimals(combinedUnreconciledAmount, 2)}`}
                  </Typography>

                  <Autocomplete
                    sx={{ mt: 2, width: '100%' }}
                    options={contracts || []}
                    getOptionLabel={option =>
                      option ? getContractDescription(option) : ''
                    }
                    value={values.contract}
                    onChange={(event, value) => {
                      setFieldValue('contract', value);
                      if (value?.id && values.isPayoffRequest) {
                        dispatch(
                          loadPayoffAmountAction(value.id, newAmount =>
                            setFieldValue('amount', newAmount)
                          )
                        );
                      } else if (!value) {
                        setFieldValue('amount', '');
                      } else if (value && !values.isPayoffRequest) {
                        setFieldValue(
                          'amount',
                          unreconciledAmount != null
                            ? String(unreconciledAmount)
                            : ''
                        );
                      }
                    }}
                    loading={isLoadingContracts}
                    loadingText={t('COMMON.LOADING')}
                    disabled={isLoadingContracts}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={t('COMMON.CONTRACT')}
                        variant="outlined"
                        placeholder={t('COMMON.SEARCH')}
                        error={Boolean(touched.contract && errors.contract)}
                        helperText={
                          touched.contract && errors.contract
                            ? t(errors.contract)
                            : ''
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Typography variant="body1">
                          {getContractDescription(option)}
                        </Typography>
                      </Box>
                    )}
                    noOptionsText={t(
                      'SANTANDER_REPORTS.NO_ACTIVE_CONTRACTS_FOUND'
                    )}
                  />

                  <TextField
                    fullWidth
                    name="amount"
                    InputProps={{
                      autoComplete: 'off',
                      inputComponent: MoneyMask,
                      readOnly: values.isPayoffRequest
                    }}
        InputLabelProps={{ shrink: true }}
                    sx={{ mt: 2 }}
                    label={t('SANTANDER_REPORTS.AMOUNT')}
                    variant="outlined"
                    value={values.amount}
                    onChange={e => setFieldValue('amount', e.target.value)}
                    onBlur={handleBlur}
                    error={Boolean(showAmountError)}
                    helperText={showAmountError ? t(errors.amount) : ''}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        name="payoffRequest"
                        color="primary"
                        checked={values.isPayoffRequest}
                        onChange={() => {
                          const isPayoff = !values.isPayoffRequest;
                          setFieldValue('isPayoffRequest', isPayoff);
                          if (isPayoff && values.contract?.id) {
                            dispatch(
                              loadPayoffAmountAction(
                                values.contract.id,
                                newAmount => setFieldValue('amount', newAmount)
                              )
                            );
                          } else {
                            setRelatedPayments([]);
                            setFieldValue(
                              'amount',
                              unreconciledAmount != null
                                ? String(unreconciledAmount)
                                : ''
                            );
                          }
                        }}
                      />
                    }
                    label={t('COMMON.PAYOFF_REQUEST')}
                    sx={{ marginTop: 2 }}
                  />

                  {values.isPayoffRequest && (
                    <Autocomplete
                      multiple
                      sx={{ mt: 2, width: '100%' }}
                      options={otherPayments}
                      value={relatedPayments}
                      onChange={(event, value) => setRelatedPayments(value)}
                      getOptionLabel={option =>
                        `$${formatAmountWithDecimals(option.unreconciledAmount, 2)} - CLABE ${option.incomeClabe}`
                      }
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={t('SANTANDER_REPORTS.RELATED_PAYMENTS_LABEL')}
                          variant="outlined"
                          placeholder={t('COMMON.SEARCH')}
                        />
                      )}
                      noOptionsText={t(
                        'SANTANDER_REPORTS.RELATED_PAYMENTS_NO_OPTIONS'
                      )}
                    />
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      mt: 3
                    }}
                  >
                    <Button
                      onClick={handleClose}
                      color="primary"
                      variant="outlined"
                      sx={{ flex: 1 }}
                      disabled={isLoadingSubmit}
                    >
                      {t('COMMON.CANCEL')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ flex: 1 }}
                      disabled={submitDisabled}
                    >
                      {isLoadingSubmit
                        ? t('COMMON.LOADING')
                        : t('SANTANDER_REPORTS.CREATE_CONTRIBUTION')}
                    </Button>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

CreateCapitalContributionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  paymentData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    client: PropTypes.shape({
      id: PropTypes.number.isRequired,
      fullname: PropTypes.string.isRequired
    }).isRequired,
    unreconciledAmount: PropTypes.number
  }).isRequired,
  otherPayments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      unreconciledAmount: PropTypes.number,
      incomeClabe: PropTypes.string
    })
  ),
  reportId: PropTypes.string.isRequired
};

CreateCapitalContributionModal.defaultProps = {
  otherPayments: []
};

export default CreateCapitalContributionModal;
